import { STYLES, REFERENCES, NS } from "../constants";
import type { IPromptBoxOptions, IFileSelectButtonSettings } from "../models";
import type { IPromptBoxInternalOptions } from "../models/options.model";
import { htmlService } from "../../core/services/html.service";
import { utilsService } from "../../core/services/utils.service";
import { fileUtilsService } from "../../core/services/file-utils.service";
import { domUtilsService } from "../../core/services/dom-utils.service";
import type { DragToScrollHandler } from "../../core/models/dom-utils";
import type { IKendoJQuery } from "../../core";
import type { IFile } from "../../upload";

declare const kendo: any;

export interface IFileHandlerCallbacks {
    onFilesChanged: () => void;
    onFileSelect: (files: IFile[]) => void;
    onFileRemove: (file: IFile, remainingFiles: IFile[]) => void;
}

export interface IFileHandlerContext {
    wrapper: IKendoJQuery;
    getOptions: () => IPromptBoxOptions;
    callbacks: IFileHandlerCallbacks;
}

export class FileHandler {
    private context: IFileHandlerContext;
    private files: IFile[] = [];
    private uploadInstance?: any;
    private _dragHandler: DragToScrollHandler | null = null;

    constructor(context: IFileHandlerContext) {
        this.context = context;
    }

    initUpload(): void {
        const options = this.context.getOptions();
        if (!this.showFileSelectButton(options)) {
            return;
        }

        const fileInput = this.context.wrapper.find(`[${REFERENCES.fileInput}]`);
        if (fileInput.length === 0) {
            return;
        }

        const settings = this.getFileSelectButtonSettings(options);
        const multiple = settings?.multiple !== false;

        const upload = new kendo.ui.Upload(fileInput, {
            multiple: multiple,
            async: false,
            uniqueFileUids: true,
            select: this.onUploadSelect.bind(this)
        });

        upload?.wrapper?.addClass(STYLES.hidden);
        this.uploadInstance = upload;
    }

    getFiles(): IFile[] {
        return [...this.files];
    }

    setFiles(newFiles: IFile[]): void {
        this.files = [...newFiles];
        this.renderAttachments();
        this.context.callbacks.onFilesChanged();
    }

    clearFiles(): void {
        this.files = [];
        this.renderAttachments();
        this.context.callbacks.onFilesChanged();
    }

    hasFiles(): boolean {
        return this.files.length > 0;
    }

    handleFileSelectClick(): void {
        const options = this.context.getOptions();
        const settings = this.getFileSelectButtonSettings(options);

        if (!options.enable || options.readonly || settings?.enable === false) {
            return;
        }

        const uploadInput = this.uploadInstance?.element?.[0] || null;
        const fileInput = uploadInput || this.context.wrapper.find(`[${REFERENCES.fileInput}]`)[0];

        if (fileInput && typeof (fileInput as HTMLInputElement).click === "function") {
            (fileInput as HTMLInputElement).click();
        }
    }

    removeAttachmentByUid(uid: string): void {
        const removedFile = this.files.find(file => this.getFileUid(file) === uid);
        const newFiles = this.files.filter(file => this.getFileUid(file) !== uid);

        if (newFiles.length === this.files.length) {
            return;
        }

        this.files = newFiles;
        this.renderAttachments();
        this.context.callbacks.onFilesChanged();

        if (removedFile) {
            this.context.callbacks.onFileRemove(removedFile, [...this.files]);
        }
    }

    renderAttachments(): void {
        this._destroyDragHandler();

        const options = this.context.getOptions() as IPromptBoxInternalOptions;
        const header = this.context.wrapper.find(`[${REFERENCES.header}]`);
        const host = this.context.wrapper.find(`[${REFERENCES.attachmentsHost}]`);
        if (host.length === 0) {
            return;
        }

        if (!this.files.length) {
            host.empty();

            if (!options.headerTemplate) {
                header.addClass(STYLES.hidden);
            }
            return;
        }

        header.removeClass(STYLES.hidden);

        if (options._filesTemplate) {
            host.html(options._filesTemplate(this.files));
            this._attachDragToScroll();
            return;
        }

        let html = `<ul class="${STYLES.fileBoxWrapper} ${STYLES.fileBoxWrapperScrollableStart}">`;
        html += `<div class="${STYLES.filesScroll}">`;

        for (let i = 0; i < this.files.length; i++) {
            const fileObj = this.files[i];
            const rawFile = fileObj?.rawFile || fileObj;
            const uid = this.getFileUid(fileObj);
            const name = rawFile?.name || fileObj?.name || "";
            const size = rawFile?.size || fileObj?.size || 0;
            const extension = name.lastIndexOf(".") > -1 ? name.substring(name.lastIndexOf(".")).toLowerCase() : "";
            const iconName = fileUtilsService.getFileGroup(extension, true);
            const fileSizeMessage = fileUtilsService.getFileSizeMessage(size);
            const icon = kendo.ui.icon({ icon: iconName, size: "xlarge" });
            const removeButton = kendo.html.renderButton(
                `<button type="button" ${REFERENCES.attachmentRemoveButton} data-uid="${htmlService.encode(uid)}" aria-label="Remove attachment" title="Remove attachment"></button>`,
                { icon: "x-mark-sm-outline", fillMode: "flat" }
            );

            html += `<li class="${STYLES.fileBox}">`;
            html += icon;
            html += `<div class="${STYLES.fileInfo}">`;
            html += `<span class="${STYLES.fileName}">${htmlService.encode(name)}</span>`;
            html += `<span class="${STYLES.fileSize}">${htmlService.encode(fileSizeMessage)}</span>`;
            html += `</div>`;
            html += removeButton;
            html += `</li>`;
        }

        html += "</div></ul>";
        host.html(html);

        this._attachDragToScroll();
    }

    private _attachDragToScroll(): void {
        const host = this.context.wrapper.find(`[${REFERENCES.attachmentsHost}]`);
        const scrollContainer = host.find("." + STYLES.filesScroll) as IKendoJQuery;
        if (!scrollContainer.length) {
            return;
        }

        this._dragHandler = domUtilsService.createDragToScrollHandler(
            scrollContainer,
            {
                namespace: NS + ".promptBoxFileDrag",
                captureElement: this.context.wrapper
            }
        );
        this._dragHandler.attach();
    }

    private _destroyDragHandler(): void {
        if (this._dragHandler) {
            this._dragHandler.destroy();
            this._dragHandler = null;
        }
    }

    destroy(): void {
        this._destroyDragHandler();
        if (this.uploadInstance) {
            this.uploadInstance.destroy();
            this.uploadInstance = null;
        }
    }

    private onUploadSelect(e: any): void {
        e.preventDefault();
        const files = e.files;
        const options = this.context.getOptions();
        const settings = this.getFileSelectButtonSettings(options);
        const validFiles = this.validateFiles(files, settings);

        if (validFiles.length > 0) {
            this.files = [...this.files, ...validFiles];
            this.renderAttachments();
            this.context.callbacks.onFilesChanged();
            this.context.callbacks.onFileSelect(validFiles);
        }
    }

    private validateFiles(files: IFile[], settings?: IFileSelectButtonSettings): IFile[] {
        const validFiles: IFile[] = [];
        const restrictions = settings?.restrictions;

        for (let i = 0; i < files.length; i++) {
            const fileObj = files[i];
            const file = fileObj.rawFile || fileObj;
            let isValid = true;

            if (restrictions) {
                if (restrictions.allowedExtensions && restrictions.allowedExtensions.length > 0) {
                    const ext = file.name.split('.').pop()?.toLowerCase() || '';
                    const allowedExts = restrictions.allowedExtensions.map((e: string) => e.toLowerCase().replace('.', ''));
                    if (!allowedExts.includes(ext)) {
                        isValid = false;
                    }
                }

                if (restrictions.maxFileSize && file.size > restrictions.maxFileSize) {
                    isValid = false;
                }

                if (restrictions.minFileSize && file.size < restrictions.minFileSize) {
                    isValid = false;
                }
            }

            if (isValid) {
                validFiles.push(fileObj);
            }
        }

        return validFiles;
    }

    private getFileUid(file: IFile): string {
        if (!file) {
            return "";
        }

        if (!file.uid) {
            file.uid = utilsService.guid();
        }

        return file.uid;
    }

    private showFileSelectButton(options: IPromptBoxOptions): boolean {
        return options.fileSelectButton === true ||
            (typeof options.fileSelectButton === "object" && options.fileSelectButton !== null);
    }

    private getFileSelectButtonSettings(options: IPromptBoxOptions): IFileSelectButtonSettings | undefined {
        if (typeof options.fileSelectButton === "object" && options.fileSelectButton !== null) {
            return options.fileSelectButton as IFileSelectButtonSettings;
        }
        return undefined;
    }
}
